import React, { useState } from "react"
import { useMutation } from "@apollo/client/react"
import { CreateEmailLeadMutation } from "@love-irish/graphql-api"
import analytics, { ANALYTICS_EVENT_NAMES } from "@love-irish/analytics"
import { usePageProps } from "@love-irish/application-page-context"
import {
  NewsletterSignupTemplate,
  type LeadType,
} from "./newsletter_signup_template"
import type { EmailLeadAttributionInput } from "./types"

// =============================================================================
// Re-export template and types
// =============================================================================

export {
  NewsletterSignupTemplate,
  LEAD_COPY,
} from "./newsletter_signup_template"
export type {
  NewsletterSignupTemplateProps,
  LeadType,
} from "./newsletter_signup_template"
export type { EmailLeadAttributionInput } from "./types"

// =============================================================================
// Inline mutation response types
// (generated types not yet available — re-run graphql codegen after the
// createEmailLead mutation + EmailLeadAttributionInput land in the schema)
// =============================================================================

interface CreateEmailLeadMutationResponse {
  createEmailLead: {
    success: boolean
    errors?: Array<{
      path: string[]
      message: string
    }>
  }
}

// =============================================================================
// Connected component props
// =============================================================================

export interface NewsletterSignupProps {
  /** Which lead flow — selects default copy + tags the lead. Default "newsletter". */
  leadType?: LeadType
  /** Heading shown inside the card */
  title?: string
  /** Subtext shown below the title */
  description?: React.ReactNode
  /** Submit button label */
  cta?: string
  // Page context forwarded to the mutation (usually supplied via PagePropsProvider, not props)
  path?: string
  /** Server-issued signed GlobalID of the page's resource (decoded server-side) */
  resourceSgid?: string
  /** Placement/component this signup renders in (e.g. "homepage-mission") — for source attribution */
  source?: string
}

// =============================================================================
// Connected Component (handles Apollo mutation)
// =============================================================================

export const NewsletterSignup: React.FC<NewsletterSignupProps> = ({
  leadType = "newsletter",
  title,
  description,
  cta,
  path,
  resourceSgid,
  source,
}) => {
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [createLead, { loading }] =
    useMutation<CreateEmailLeadMutationResponse>(CreateEmailLeadMutation)

  // Current page's props (path + signed resource GID) from context; explicit props override them.
  const page = usePageProps()

  const attribution: EmailLeadAttributionInput | undefined = (() => {
    const merged: EmailLeadAttributionInput = {
      path: path ?? page.path,
      resourceSgid: resourceSgid ?? page.resourceSgid,
      source,
    }
    // Only send the fields we actually have (drop undefined/null).
    const entries = Object.entries(merged).filter(([, v]) => v != null)
    return entries.length > 0
      ? (Object.fromEntries(entries) as EmailLeadAttributionInput)
      : undefined
  })()

  const handleSubmit = async (email: string) => {
    setError(null)
    try {
      const result = await createLead({
        variables: { email, leadType, attribution },
      })

      const response = result.data?.createEmailLead

      if (response?.success) {
        setSuccess(true)
        // Fire the client-side analytics event on success (lead type + source + page context).
        analytics.track(ANALYTICS_EVENT_NAMES.NEWSLETTER_SIGNUP, {
          lead_type: leadType,
          source,
          path: path ?? page.path,
          resource_sgid: resourceSgid ?? page.resourceSgid,
        })
      } else {
        const messages = response?.errors?.map((e) => e.message) ?? []
        setError(messages[0] ?? "Something went wrong. Please try again.")
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again."
      )
    }
  }

  return (
    <NewsletterSignupTemplate
      leadType={leadType}
      title={title}
      description={description}
      cta={cta}
      onSubmit={handleSubmit}
      loading={loading}
      success={success}
      error={error}
    />
  )
}

export default NewsletterSignup
